import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const getApiUrl = () => `/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    try {
      const res = await fetch(`${getApiUrl()}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        // Only clear token on 401/403 (unauthorized/forbidden)
        if (res.status === 401 || res.status === 403) {
          console.warn('Session expired, clearing token...');
          localStorage.removeItem('token');
        }
      }
    } catch (err) {
      console.error('Fetch user error:', err);
      // Don't clear token on network errors - user might come back online
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await fetch(`${getApiUrl()}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (userData) => {
    const res = await fetch(`${getApiUrl()}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      await fetchUser(token);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const api = {
  async get(endpoint) {
    const token = localStorage.getItem('token');
    const res = await fetch(`${getApiUrl()}${endpoint}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error('API error');
    return { data: await res.json() };
  },
  async post(endpoint, body, options = {}) {
    const token = localStorage.getItem('token');
    const isFormData = body instanceof FormData;
    const res = await fetch(`${getApiUrl()}${endpoint}`, {
      method: 'POST',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: isFormData ? body : JSON.stringify(body)
    });
    let data;
    try { data = await res.json(); } catch { data = {}; }
    if (!res.ok) throw { response: { data } };
    return { data };
  },
  async put(endpoint, body) {
    const token = localStorage.getItem('token');
    const res = await fetch(`${getApiUrl()}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw { response: { data } };
    return { data };
  },
  async delete(endpoint) {
    const token = localStorage.getItem('token');
    const res = await fetch(`${getApiUrl()}${endpoint}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return { data: await res.json() };
  }
};

export default AuthContext;
